import { useState } from "react";

import {
  labeebUserLabel,
  labeebUserStandingPath,
  type LabeebUser,
} from "../../api/labeeb";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useWrite } from "../../hooks/useWrite";
import { Gate } from "../authority/Gate";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { WriteResult } from "../write/EffectTiming";
import { WriteFailure } from "../write/WriteFailure";

/**
 * Block one user of a business, or lift that block.
 *
 * The version is the one on the users row the operator is looking at — there
 * is no separate read of a user's standing. A blocked user can still sign in;
 * their next request is what gets refused.
 */
export function LabeebUserBlock({
  config,
  tenantId,
  user,
  onChanged,
}: {
  config: AppConfig;
  tenantId: string;
  user: LabeebUser;
  onChanged: () => void;
}) {
  const write = useWrite(config);
  const [confirming, setConfirming] = useState(false);
  const blocked = user.status === "blocked";
  const label = labeebUserLabel(user);
  const busy = write.state.kind === "submitting";
  const version = user.version;

  function submit(reason: string) {
    if (version === undefined) return;
    void write
      .submit(labeebUserStandingPath(tenantId, user.user_id), {
        method: "PATCH",
        body: JSON.stringify({
          expected_version: version,
          reason,
          status: blocked ? "active" : "blocked",
        }),
      })
      .then((r) => {
        if (!r) return;
        onChanged();
      });
    setConfirming(false);
  }

  return (
    <div className="mt-1">
      <Gate need="block:write">
        <Button
          outline={blocked}
          color={blocked ? undefined : "red"}
          disabled={busy || version === undefined}
          onClick={() => setConfirming(true)}
        >
          {blocked ? `Unblock ${label}…` : `Block ${label}…`}
        </Button>
      </Gate>

      {write.state.kind === "done" && (
        <div className="mt-2">
          <WriteResult title={blocked ? "Unblocked" : "Blocked"} onDismiss={write.reset}>
            <p className="text-sm">
              {blocked
                ? "Labeeb serves this person's next request."
                : "Labeeb refuses this person's next request. They can still sign in."}
            </p>
          </WriteResult>
        </div>
      )}
      <WriteFailure state={write.state} onDismiss={write.reset} onReread={onChanged} />

      <ConfirmDialog
        open={confirming}
        title={blocked ? `Unblock ${label}` : `Block ${label}`}
        confirmLabel={blocked ? "Unblock" : "Block"}
        destructive={!blocked}
        requireReason
        reasonHint="e.g. DA-1600, abuse report"
        busy={busy}
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
        effect={
          blocked
            ? "Labeeb serves this person's next request. The business's own standing is unchanged."
            : "Labeeb refuses this person's next request. They can still sign in, and the rest of the business is unaffected."
        }
      >
        <p>
          {blocked
            ? "This person can use the assistant again, subject to the business's standing."
            : "Only this person is blocked. Suspending the business is a separate control."}
        </p>
      </ConfirmDialog>
    </div>
  );
}
